import * as React from 'react';
import { connect } from 'react-redux';
import { Box } from '../../atoms/box';
import { Text } from '../../atoms/text';
import { Trans, withTranslation } from 'react-i18next';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { select } from '@rematch/select';

import { AppLayout } from '../../layouts/AppLayout';
import { CategoriesCardList } from '../../atoms/categories-card-list';
import { dispatch } from '../../store';
import { ThemeConsumer } from '../../atoms/theme';
import { SegmentedControl } from '../page-network/SegmentedControl';
import { getAppConfig } from '../../atoms/app-config-gate';
import { Loader } from '../../atoms/loader';
import { CommunityCard } from '../../atoms/community-card';
import { CourseHeader } from '../../atoms/course-header';
import { CourseCard } from '../../atoms/course-card';
import { showSuccessErrorAlert } from '../../atoms/alert';
import { CoursesService } from '../../services/CoursesService';
import { CourseDuplicate } from '../../atoms/course-duplicate';
import { Avatar, Empty } from 'ui';
import { Icon } from '../../atoms/icon';

export class PageCoursesRenderer extends React.Component {
  state = {
    openModal: false,
  };

  SCOPE = (() => {
    const featureFlags = getAppConfig().feature_flags;
    return featureFlags?.explore_branded_app
      ? [
          { value: 'joined', title: this.props.t('My Programs'), scope: 'joined', id: 'tab-my-programs' },
          {
            value: 'branded',
            title: this.props.t(`Explore ${this.props.t(this.props.brandedOrganization?.name)} Programs`),
            scope: 'branded',
            id: 'tab-explore-branded-programs',
          },
          {
            value: 'explore',
            title: this.props.t('Explore BigHeart Network Programs'),
            scope: 'explore',
            id: 'tab-explore-pba-programs',
          },
        ]
      : [
          { value: 'joined', title: this.props.t('My Programs'), scope: 'joined', id: 'tab-my-programs' },
          {
            value: 'explore',
            title: this.props.t('Explore BigHeart Network Programs'),
            scope: 'explore',
            id: 'tab-explore-pba-programs',
          },
        ];
  })();

  componentDidMount = () => {
    if (this.props.brandedOrganizationId && !this.props.brandedOrganization) {
      this.props.fetchOrganization(this.props.brandedOrganizationId);
    }

    this.fetchData();
  };

  componentDidUpdate(prevProps) {
    if (prevProps.scope !== this.props.scope) {
      this.fetchData();
    }
  }

  fetchData = async () => {
    if (this.props.scope === 'explore') {
      await this.fetchCategories();
    } else {
      this.fetchPrograms();
    }
  };

  fetchCategories = async () => {
    dispatch.categories.getAsync({ type: 'program' });
  };

  fetchPrograms = () => {
    this.props.scope === 'joined'
      ? dispatch.courses.joinedAsync()
      : dispatch.courses.notJoinedAsync(getAppConfig().organization);
  };

  onProgramClick = (id, joined) => {
    this.props.history.push(`/courses/${id}/${joined ? 'timeline' : 'about'}`);
  };

  deleteProgram = async (program) => {
    if (await window.confirm(this.props.t('Are you sure you want to delete this program?'))) {
      const response = await this.props.deleteProgram(program);
      showSuccessErrorAlert({
        isOk: response.ok,
        successMessage: <Trans>Program successfully deleted.</Trans>,
        errorMessage: response.data?.message,
      });
    }
  };

  publishProgram = async (program) => {
    if (await window.confirm(this.props.t('Are you sure you want to publish this program?'))) {
      const result = { id: program.id, status: 'published' };
      const response = await this.props.updateProgramStatus(result);
      showSuccessErrorAlert({
        isOk: response.ok,
        successMessage: <Trans>The program has been published.</Trans>,
        errorMessage: response.data?.message,
      });
    }
  };

  finishProgram = async (program) => {
    if (await window.confirm(this.props.t('Are you sure you want to archive this program?'))) {
      const result = { id: program.id, status: 'finished' };
      const response = await this.props.updateProgramStatus(result);
      showSuccessErrorAlert({
        isOk: response.ok,
        successMessage: <Trans>The program has been finished.</Trans>,
        errorMessage: response.data?.message,
      });
    }
  };

  reportProgram = async (program) => {
    if (await window.confirm(this.props.t('Are you sure you want to report this program?'))) {
      CoursesService.reportAbuse(program.id);
    }
  };

  duplicateProgram = (program) => {
    this.setState({ openModal: true, activeProgram: program });
  };

  closeModal = () => {
    this.setState({ openModal: false });
  };

  redirect = (path) => {
    this.props.history.push(path);
  };

  renderGroup = ([organization, programs]) => {
    return (
      <ThemeConsumer key={organization.id}>
        {(theme) => (
          <React.Fragment>
            <div className="group__title">
              <Avatar size={72} photo={organization.profile_photo} />
              <Text
                size={24}
                lineHeight="18px"
                color={theme.color.brand}
                weight="700"
                component="div"
                className="group__title__name"
              >
                {organization.name} <span className="group__title__count">({programs.length})</span>
              </Text>
            </div>
            <div className="group__programs-wrapper">
              {programs.map((program) => (
                <div key={program.id} className="group__program">
                  <CourseCard
                    course={program}
                    viewer={this.props.user}
                    onRequestCourse={() => this.props.history.push(`/courses/${program.id}/timeline`)}
                    onEditCourse={() => this.props.history.push(`/courses/${program.id}/update`)}
                    onDeleteCourse={() => this.deleteProgram(program)}
                    onFinishCourse={() => this.finishProgram(program)}
                    onPublishCourse={() => this.publishProgram(program)}
                    onReportCourse={() => this.reportProgram(program)}
                    onDuplicateCourse={() => this.duplicateProgram(program)}
                    myPrograms={true}
                  />
                </div>
              ))}
            </div>
          </React.Fragment>
        )}
      </ThemeConsumer>
    );
  };

  renderBranded = ([organization, programs]) => {
    return (
      <ThemeConsumer key={organization.id}>
        {() => (
          <React.Fragment>
            <Text color="#A2ABBB" size={17}>
              <Trans>Explore the programs you are not a member of</Trans>
              <span> {organization.name}</span>
            </Text>
            <TransitionGroup style={{ marginBottom: 32 }}>
              {programs.map((program, index) => (
                <CSSTransition key={index} timeout={400} classNames="fade">
                  <div className="col-md-6 col-sm-6 col-xs-6 branded-pill">
                    <CommunityCard
                      community={program}
                      onClick={() => this.onProgramClick(program.id, program.joined)}
                      className="card__branded"
                    />
                  </div>
                </CSSTransition>
              ))}
            </TransitionGroup>
          </React.Fragment>
        )}
      </ThemeConsumer>
    );
  };

  joinedPrograms = () => {
    const { programsGroupedByOrganization } = this.props;
    programsGroupedByOrganization.sort((a, b) => {
      return a[0].name.localeCompare(b[0].name);
    });

    return (
      <ThemeConsumer>
        {(theme) =>
          programsGroupedByOrganization.length ? (
            <div>
              <Box style={{ marginTop: 32 }}>
                <Text color="#A2ABBB" size={17}>
                  <Trans>Please select a program you want to go to</Trans>
                </Text>
              </Box>
              <Box flexDirection="column">{programsGroupedByOrganization.map((group) => this.renderGroup(group))}</Box>
            </div>
          ) : (
            <div className="programs__empty-state">
              <Icon name="pba-bulb" size={58} color={theme.color.brand} />
              <Text color="#001E34" size={16} weight="bold">
                <Trans>You are not currently enrolled in a program. Click “Explore” to browse programs.</Trans>
              </Text>
            </div>
          )
        }
      </ThemeConsumer>
    );
  };

  brandedPrograms = () => {
    const { programsGroupedByOrganization } = this.props;

    return (
      <ThemeConsumer>
        {(theme) =>
          programsGroupedByOrganization.length ? (
            <div>
              <Box style={{ marginTop: 32 }}>
                <Text color={theme.color.brand} size={28} style={{ fontWeight: 'bold', marginBottom: 16 }}>
                  <Trans>Explore more programs</Trans>
                </Text>
                {programsGroupedByOrganization.map((group) => this.renderBranded(group))}
              </Box>
            </div>
          ) : (
            <Empty description={<Trans>There are no programs to explore yet</Trans>} />
          )
        }
      </ThemeConsumer>
    );
  };

  header = () => {
    return (
      <ThemeConsumer>
        {(theme) =>
          !theme.isBranded ? (
            <Box>
              <Text color={theme.color.brand} size={28} style={{ fontWeight: 'bold', marginBottom: 16 }}>
                <Trans>What type of program are you looking for?</Trans>
              </Text>
              <Text color={theme.color.lightGray} size={17}>
                <Trans>Select category to get started</Trans>
              </Text>
            </Box>
          ) : null
        }
      </ThemeConsumer>
    );
  };

  render() {
    const { explore_branded_app } = getAppConfig().feature_flags;
    const { user, history } = this.props;

    return (
      <AppLayout
        center={
          <ThemeConsumer>
            {() => (
              <React.Fragment>
                <CourseHeader user={user} history={history} />
                <div>
                  <SegmentedControl
                    id="programs-tabs"
                    labels={this.SCOPE}
                    selectedLabel={this.props.scope}
                    onChange={(label) => {
                      this.props.history.push(`/courses/${label}`);
                    }}
                  />
                </div>
                {!this.props.loading && !this.props.loadingCategories ? (
                  <div style={{ paddingTop: 15 }}>
                    {this.props.scope === 'joined' ? (
                      this.joinedPrograms()
                    ) : this.props.scope === 'branded' && explore_branded_app ? (
                      this.brandedPrograms()
                    ) : (
                      <CategoriesCardList header={this.header} type="program" />
                    )}
                  </div>
                ) : (
                  <Loader />
                )}
                <CourseDuplicate
                  isDuplicateOpen={this.state.openModal}
                  onRequestClose={this.closeModal}
                  program={this.state.activeProgram}
                  redirect={() => this.redirect}
                />
              </React.Fragment>
            )}
          </ThemeConsumer>
        }
      />
    );
  }
}

const mapState = (state, props) => ({
  user: select.session.user(state),
  scope: props.match.params.scope,
  loading: state.loading.models.courses,
  programsGroupedByOrganization: select.courses.getGroupedByOrganization(state),
  brandedOrganizationId: getAppConfig().organization?.id,
  brandedOrganization: getAppConfig().organization && select.organizations.find(state, getAppConfig().organization.id),
  loadingCategories: state.loading.effects.categories.getAsync,
});

const mapDispatch = (dispatch) => ({
  fetchOrganization: (id) => dispatch.organizations.findAsync(id),
  deleteProgram: (program) => dispatch.adminCourses.delete(program),
  updateProgramStatus: (program) => dispatch.courses.updateStatus(program),
});

export const PageCourses = withTranslation()(connect(mapState, mapDispatch)(PageCoursesRenderer));
